/**
 * OPUS-1619 — turn a scene caption's `data` into the block the Broker expects.
 *
 * THE TWO SHAPES ARE NOT THE SAME, and that is the whole job of this file.
 *
 * A scene caption (…/<graphic>/<version>/captions/<name>.json) carries:
 *
 *   { "data": { "$fixture": "fixture[0]", … 39 more $vars …,
 *               "Main": { "Team1": { … } } } }
 *
 * The field tree sits at the TOP level as `Main`, because the caption's own
 * path already says which graphic it belongs to. The Broker has no such
 * context — it splits a caption body by caption id — so it needs:
 *
 *   { "$fixture": "fixture[0]", …,
 *     "gfx_LF_Scoreline": { "Main": { "Team1": { … } } } }
 *
 * So: keep the $vars where they are (they form the shared binding scope) and
 * nest everything else under the layer id.
 *
 * Also handles a block that is ALREADY in Broker shape, so a definition
 * authored either way works.
 */

// JSON Schema's own $ keys. Without excluding these, a plain OGRAF graphic
// definition scores on its `$schema` alone and gets mistaken for a caption.
const SCHEMA_KEYS = new Set(['$schema', '$ref', '$defs', '$id', '$comment', '$anchor', '$vocabulary']);

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** A real JSONata variable definition, not a JSON Schema keyword. */
const jsonataVars = (obj) =>
  Object.keys(obj).filter((k) => k.startsWith('$') && !SCHEMA_KEYS.has(k) && typeof obj[k] === 'string');

/** Everything that is not a $var — the field tree, e.g. `Main`. */
const treeKeys = (obj) =>
  Object.keys(obj).filter((k) => !k.startsWith('$') && isPlainObject(obj[k]));

/**
 * @param {object} data     a caption's `data`, either shape
 * @param {string} layer    e.g. 'gfx_LF_Scoreline'
 * @returns {object|null}   the block in Broker shape
 */
export function toBrokerBlock(data, layer) {
  if (!isPlainObject(data)) return null;

  const trees = treeKeys(data);
  if (trees.length === 0) return null;

  // Already Broker-shaped: a gfx_* key holds the tree. Pass it through rather
  // than double-wrapping it.
  if (trees.some((k) => k.startsWith('gfx_'))) return data;

  if (!layer) return null;

  const vars = {};
  const tree = {};
  for (const [k, v] of Object.entries(data)) {
    if (k.startsWith('$')) vars[k] = v;
    else tree[k] = v;
  }
  return { ...vars, [layer]: tree };
}

/**
 * Is this object a caption data block at all?
 *
 * Needs a field tree, plus either a gfx_* key or at least one real JSONata
 * var. That second arm is what lets a scene caption qualify — it has 39 vars
 * and a bare `Main`, and no gfx_* key anywhere.
 */
function score(obj, layer) {
  if (!isPlainObject(obj)) return 0;

  const trees = treeKeys(obj);
  if (trees.length === 0) return 0;

  const named = trees.includes(layer);
  const anyGraphic = trees.some((k) => k.startsWith('gfx_'));
  const vars = jsonataVars(obj).length;

  if (!anyGraphic && vars === 0) return 0;
  return (named ? 1000 : 0) + (anyGraphic ? 100 : 0) + vars;
}

/**
 * Find a data block anywhere in a document and return it in Broker shape.
 *
 * Found by SHAPE, not by key name, so a block nested under whatever field the
 * scene authors chose still works.
 *
 * @returns {{ block: object, path: string }|null}
 */
export function findSceneDataBlock(definition, layer) {
  if (!isPlainObject(definition)) return null;

  let best = null;

  const walk = (node, path, depth) => {
    if (depth > 8 || !isPlainObject(node)) return;

    const s = score(node, layer);
    if (s > 0 && (!best || s > best.score)) best = { node, path, score: s };

    for (const [k, v] of Object.entries(node)) {
      // Never descend into a field tree — its children would score as weaker
      // duplicates of their own parent.
      if (k.startsWith('gfx_') || k === 'Main') continue;
      if (isPlainObject(v)) walk(v, `${path}/${k}`, depth + 1);
    }
  };

  walk(definition, '', 0);
  if (!best) return null;

  const block = toBrokerBlock(best.node, layer);
  return block ? { block, path: best.path || '/' } : null;
}

export default findSceneDataBlock;
